import { CommonModule } from '@angular/common';
import {Component, OnDestroy, OnInit, TemplateRef} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, MenuItemCommandEvent, MessageService } from 'primeng/api';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { AuthenticationManagerService } from '../../../services/authentication-manager.service';
import { AuthService, LoadingPanelService, LocalService } from 'lib-base';
import {ButtonDirective} from "primeng/button";
import {OverlayPanel, OverlayPanelModule} from "primeng/overlaypanel";
import {AvatarModule} from "primeng/avatar";
import {WebContribuyenteService} from "../../../services/webContribuyenteService.service";
import {IGetInformacionGeneralContribuyente} from "../../../shared/interfaces/seguridad";
import {MasterMenuItemsService} from "../../../services/menu/master-menu-items.service";

@Component({
  selector: 'app-pub-topbar',
  standalone: true,
  imports: [CommonModule, RouterModule, ButtonDirective, OverlayPanelModule, AvatarModule],
  templateUrl: './pub-topbar.component.html',
  styleUrl: './pub-topbar.component.scss'
})
export class PubTopbarComponent implements OnInit, OnDestroy {

  showMenuMobile = false;
  dynamicTemplate! :TemplateRef<any>;
  menuItems !: MenuItem[];
  private _translations !: any;
  userName:string = '';
  shortName:string = '';
  loginSubscription !: Subscription;
  logoutSubscription !: Subscription;

  constructor(private _translate: TranslateService, private _am: AuthenticationManagerService, private _ls: LocalService, private _as: AuthService,
    private _ms: MessageService, private _r: Router, private _lps: LoadingPanelService, private masterMenuItems: MasterMenuItemsService) {
    this.getUserNames();
    this.loginSubscription = this._as.loginSource$.subscribe(key => {
      this.initializeOptions();
      this.getUserNames();
    });
    this.logoutSubscription = this._as.logoutSource$.subscribe(key => {
      this.initializeOptions();
      this._r.navigate(['/public/consulta']);
      this._ms.add({ severity: 'success', summary: this._translations.finSesion, detail: this._translations.loggedOut });
    });
  }

  ngOnDestroy(): void {
    if (this.loginSubscription) {
      this.loginSubscription.unsubscribe();
    }
    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
    }
  }

  ngOnInit() {
    this.translate();
  }

  private async translate(): Promise<void> {
    this._translations = await firstValueFrom(forkJoin({
      consultaTramites: this._translate.get('components.app-pub-topbar.consulta-tramites'),
      estadoTramite: this._translate.get('components.app-consulta-tramites.ver-estado-tramites'),
      estadoTramites: this._translate.get('components.app-pub-topbar.estado-tramites'),
      firmaElectronica: this._translate.get('components.app-pub-topbar.firma-electronica'),
      firmaElectronicaSolicitud: this._translate.get('components.app-pub-topbar.firma-electronica-solicitud'),
      firmaElectronicaRenovacion: this._translate.get('components.app-pub-topbar.firma-electronica-renovacion'),
      firmaElectronicaRevocacion: this._translate.get('components.app-pub-topbar.firma-electronica-revocacion'),
      pqrsd: this._translate.get('components.app-pub-topbar.pqrsd'),
      iniciarSesion: this._translate.get('components.app-pub-topbar.iniciar-sesion'),
      cerrarSesion: this._translate.get('components.app-pub-topbar.cerrar-sesion'),
      loggedOut: this._translate.get('components.app-pub-topbar.logged-out'),
      finSesion: this._translate.get('components.app-pub-topbar.fin-sesion'),
      misFacturas: this._translate.get('common.misFacturas'),
      misPagos: this._translate.get('common.misPagos'),
      verPerfil: this._translate.get('components.app-pub-topbar.ver-perfil'),
    }));
    this.initializeOptions();
  }

  initializeOptions() {
    let test: any = document.getElementById("p-tax-button-mobile");
    let dash: any = document.getElementById('p-visible-mobile');
    const isLoggedIn: boolean = this._ls.isLoggedIn;
    const itemsApp = this.masterMenuItems.getMenuItems(this._translations, isLoggedIn);
    const firstItemsMenu = [
      { label: this._translations.consultaTramites, icon: 'pi pi-search', routerLink: ['/'], visible: true },
      { label: this._translations.estadoTramite, icon: 'pi pi-sync', routerLink: ['/public/estado-tramite'], visible: true },
      { label: this._translations.estadoTramites, icon: 'icon-taxes-house', routerLink: ['/tramites/estado-tramite'], visible:  isLoggedIn },
    ];
    const lastItemsMenu = [
      {separator: true, visible:true},
      { label: this._translations.pqrsd, icon: 'icon-taxes-declarar', routerLink: ['/pqrsdf'], visible: true },
      { label: this._translations.iniciarSesion, icon: 'pi pi-sign-in',
        command: (event) => this.onIniciarSesion(event), routerLink: [], visible: !isLoggedIn },
      { label: this._translations.cerrarSesion, icon: 'pi pi-sign-out', styleClass:'text-red-600',
        command: (event) => this.onLogout(event), routerLink: [], visible: isLoggedIn }
    ] as MenuItem[];

    this.menuItems = [...firstItemsMenu, ...itemsApp, ...lastItemsMenu];
    if (test) {
      test?.addEventListener("mouseenter", function (event: MouseEvent) {
        (dash as HTMLElement).style.display = "flex";
        // highlight the mouseenter target
        dash.style.display = "flex";
      }, false);

      test?.addEventListener("mouseleave", function (event: MouseEvent) {
        // highlight the mouseenter target
        dash.style.display = "none";
      }, false);
    }
  }

  onIniciarSesion(event: MenuItemCommandEvent) {
    this._am.onMenuOpen();
  }

  onLogout(event: MenuItemCommandEvent) {
    this._lps.show(true);
    this._as.logout().subscribe({
      next: (response) => {
        this._lps.hide();
      }
    });
  }

  verPerfil(){
    this._r.navigate(['/tramites','perfil'])
  }
  openOverlay(overlayPanel:OverlayPanel, temp: TemplateRef<any>, event: MouseEvent) {
     this.dynamicTemplate = temp;
     overlayPanel.toggle(event);
  }

  getUserNames(): void {
    this.userName = this._ls.getUser.fullName;
    this.shortName = this.userName ? this.userName[0]?.toUpperCase() : "";
  }

  get verPerfilLabel(): string {
    return this._translations.verPerfil;
  }
  get isLoggedIn(): boolean {
    return this._ls.isLoggedIn;
  }
}
